import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import { useTheme } from '../../../../context/ThemeContext';
import { usePlatformIntegrationAuth } from '../../../../hooks/usePlatformIntegrationAuth';
import {
  IntegrationLoadingSpinner,
  IntegrationSetupNotice,
  integrationT,
  isIntegrationFetchFailure,
} from '../../../../components/platform/IntegrationFetchState';
import {
  buildPlatformMailgunSavePayload,
  createEmptyMailgunFormFields,
  resolveWebhookSigningKeyConfigured,
} from '../../../../utils/platformMailgunSettingsForm';
import {
  Mail,
  Settings,
  Shield,
  Server,
  Globe,
  Check,
  X,
  Eye,
  EyeOff,
  Send,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Info,
  ExternalLink,
  Copy,
  Zap,
  Activity,
  FileText,
  Key,
  ArrowRight,
  ChevronRight,
  Trash2,
  ArrowUpCircle,
  Fingerprint,
} from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import {
  platformSaveButtonClassName,
} from '../../../../components/platform/PlatformSaveButtonStyles';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import { Switch } from '../../../../components/ui/switch';
import { Label } from '../../../../components/ui/label';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../../components/ui/alert-dialog';
import {
  DomainVerificationPanel,
} from '../../../../components/platform/email/EmailDomainComponents';

// Normalize backend URL to prevent double /api paths
const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '').replace(/\/api$/, '');
const API = `${API_BASE}/api`;

const DEFAULT_UNAVAILABLE_SETTINGS = {
  provider: 'mailgun',
  configured: false,
  enabled: false,
  status: 'not_configured',
  health_status: 'not_configured',
  is_active: false,
  active_key: null,
  pending_key: null,
  domain_status: {
    spf: 'not_checked',
    dkim: 'not_checked',
    tracking_cname: 'not_checked',
    dmarc: 'not_checked',
  },
};

const EndpointUnavailableBanner = ({ isDark, onRetry, retrying }) => (
  <div
    className={`flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl border ${
      isDark ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'
    }`}
    data-testid="mailgun-endpoint-unavailable-banner"
  >
    <div className="flex items-start gap-3 flex-1 min-w-0">
      <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
      <p className={`text-sm ${isDark ? 'text-zinc-300' : 'text-stone-700'}`}>
        {integrationT('platformSettings.integrations.mailgun.messages.settingsEndpointUnavailable')}
      </p>
    </div>
    <Button
      variant="outline"
      size="sm"
      onClick={onRetry}
      disabled={retrying}
      className={isDark ? 'border-zinc-700 shrink-0' : 'shrink-0'}
      data-testid="mailgun-config-retry-btn"
    >
      <RefreshCw size={16} className={`mr-2 ${retrying ? 'animate-spin' : ''}`} />
      {integrationT('platformSettings.integrations.common.actions.retry')}
    </Button>
  </div>
);

// ============ STATUS BADGE COMPONENT ============
const HealthStatusBadge = ({ status }) => {
  const statusConfig = {
    active: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle2, label: 'Active' },
    connected: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: Check, label: 'Connected' },
    not_configured: { bg: 'bg-zinc-600/50', text: 'text-zinc-400', icon: X, label: 'Not Configured' },
    warning: { bg: 'bg-amber-500/20', text: 'text-amber-400', icon: AlertTriangle, label: 'Warning' },
    failing: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle, label: 'Failing' },
    inactive: { bg: 'bg-zinc-500/20', text: 'text-zinc-400', icon: X, label: 'Inactive' },
  };

  const config = statusConfig[status] || statusConfig.not_configured;
  const Icon = config.icon;

  return (
    <Badge className={`${config.bg} ${config.text} border-0 flex items-center gap-1.5`}>
      <Icon className="w-3.5 h-3.5" />
      {config.label}
    </Badge>
  );
};

// ============ KEY TEST STATUS BADGE ============
const KeyTestStatusBadge = ({ status }) => {
  const statusConfig = {
    passed: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle2, label: 'Passed' },
    failed: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle, label: 'Failed' },
    pending: { bg: 'bg-amber-500/20', text: 'text-amber-400', icon: Clock, label: 'Awaiting Validation' },
    validated: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle2, label: 'Domain Access OK' },
    untested: { bg: 'bg-zinc-600/50', text: 'text-zinc-400', icon: Clock, label: 'Not Tested' },
    success: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle2, label: 'Passed' },
  };

  const config = statusConfig[status] || statusConfig.untested;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

// ============ ACTIVE KEY CARD ============
const ActiveKeyCard = ({ keyInfo, onTest, onReplace, testing, persistDisabled = false }) => {
  if (!keyInfo || !keyInfo.masked) {
    return (
      <Card className="bg-zinc-900/50 border-zinc-800" data-testid="active-key-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/20 rounded-lg">
              <Key className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <CardTitle className="text-white text-base">Active API Key</CardTitle>
              <CardDescription className="text-zinc-400 text-sm">Currently used for sending emails</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700 border-dashed text-center">
            <Key className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
            <p className="text-zinc-400 text-sm">No API key configured</p>
            <Button
              size="sm"
              onClick={onReplace}
              className="mt-3 bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="add-first-key-btn"
            >
              Add API Key
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900/50 border-emerald-500/30" data-testid="active-key-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/20 rounded-lg">
              <Key className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <CardTitle className="text-white text-base">Active API Key</CardTitle>
              <CardDescription className="text-zinc-400 text-sm">Currently used for sending emails</CardDescription>
            </div>
          </div>
          <KeyTestStatusBadge status={keyInfo.test_status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Masked Key Display */}
        <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-zinc-500 uppercase mb-1">Masked Key</p>
              <code className="text-emerald-400 font-mono text-sm">{keyInfo.masked}</code>
            </div>
            <div className="text-right">
              <p className="text-xs text-zinc-500 uppercase mb-1">Fingerprint</p>
              <div className="flex items-center gap-1 text-zinc-300">
                <Fingerprint className="w-3.5 h-3.5 text-zinc-400" />
                <code className="font-mono text-sm">{keyInfo.fingerprint || 'N/A'}</code>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metadata */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-zinc-500 uppercase">Saved</p>
            <p className="text-sm text-zinc-300">
              {keyInfo.saved_at ? new Date(keyInfo.saved_at).toLocaleDateString() : 'Unknown'}
            </p>
          </div>
          <div>
            <p className="text-xs text-zinc-500 uppercase">Last Tested</p>
            <p className="text-sm text-zinc-300">
              {keyInfo.tested_at ? new Date(keyInfo.tested_at).toLocaleString() : 'Never'}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2 border-t border-zinc-800">
          <Button
            size="sm"
            onClick={onTest}
            disabled={testing || persistDisabled}
            className="flex-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/30"
            data-testid="test-active-key-btn"
          >
            {testing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Test Active Key
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onReplace}
            className="border-zinc-600 text-zinc-300 hover:bg-zinc-800"
            data-testid="replace-key-btn"
          >
            Replace Key
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ============ PENDING KEY CARD ============
const PendingKeyCard = ({
  keyInfo,
  mailgunDomain,
  onValidateAccess,
  onTest,
  onPromote,
  onCancel,
  validating,
  testing,
  promoting,
  cancelling,
  persistDisabled = false,
}) => {
  if (!keyInfo || !keyInfo.masked) {
    return null;
  }

  const canPromote = keyInfo.access_status === 'validated';
  const accessStatus = keyInfo.access_status || 'pending';

  return (
    <Card className="bg-zinc-900/50 border-amber-500/30" data-testid="pending-key-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <CardTitle className="text-white text-base">Pending Replacement Key</CardTitle>
              <CardDescription className="text-zinc-400 text-sm">
                Validate domain API access before activation
              </CardDescription>
            </div>
          </div>
          <KeyTestStatusBadge status={accessStatus} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Masked Key Display */}
        <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-zinc-500 uppercase mb-1">Masked Key</p>
              <code className="text-amber-400 font-mono text-sm">{keyInfo.masked}</code>
            </div>
            <div className="text-right">
              <p className="text-xs text-zinc-500 uppercase mb-1">Fingerprint</p>
              <div className="flex items-center gap-1 text-zinc-300">
                <Fingerprint className="w-3.5 h-3.5 text-zinc-400" />
                <code className="font-mono text-sm">{keyInfo.fingerprint || 'N/A'}</code>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metadata */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-zinc-500 uppercase">Added</p>
            <p className="text-sm text-zinc-300">
              {keyInfo.saved_at ? new Date(keyInfo.saved_at).toLocaleDateString() : 'Unknown'}
            </p>
          </div>
          <div>
            <p className="text-xs text-zinc-500 uppercase">Domain Access</p>
            <KeyTestStatusBadge status={accessStatus} />
          </div>
        </div>

        {keyInfo.mailgun_key_id && (
          <div className="text-xs text-zinc-500">
            Mailgun key id: <code className="text-zinc-400">{keyInfo.mailgun_key_id}</code>
          </div>
        )}

        {!canPromote && (
          <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
            <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-300">
              Validate that this key can access Mailgun domain{' '}
              <span className="font-mono">{mailgunDomain || 'your sending domain'}</span>.
              Test email is optional diagnostics only.
            </p>
          </div>
        )}

        {canPromote && (
          <div className="flex items-start gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-emerald-300">
              Key can access Mailgun domain {mailgunDomain || 'your sending domain'}.
              You can promote it to active.
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-800">
          <Button
            size="sm"
            onClick={onValidateAccess}
            disabled={validating || persistDisabled}
            className="flex-1 min-w-[10rem] bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="validate-pending-key-btn"
          >
            {validating ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Shield className="w-4 h-4 mr-2" />
            )}
            Validate Key Access
          </Button>
          <Button
            size="sm"
            onClick={onTest}
            disabled={testing || persistDisabled}
            className="flex-1 min-w-[10rem] bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30"
            data-testid="test-pending-key-btn"
          >
            {testing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Test Email (optional)
          </Button>
          <Button
            size="sm"
            onClick={onPromote}
            disabled={!canPromote || promoting || persistDisabled}
            className={`flex-1 ${canPromote 
              ? 'bg-emerald-500 hover:bg-emerald-600 text-white' 
              : 'bg-zinc-700 text-zinc-400 cursor-not-allowed'}`}
            data-testid="promote-pending-key-btn"
          >
            {promoting ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <ArrowUpCircle className="w-4 h-4 mr-2" />
            )}
            Promote to Active
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onCancel}
            disabled={cancelling || persistDisabled}
            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
            data-testid="cancel-pending-key-btn"
          >
            {cancelling ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ============ NEW KEY INPUT MODAL ============
const NewKeyModal = ({ isOpen, onClose, onSave, hasExistingKey, backendUnavailable = false }) => {
  const [newKey, setNewKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [modalError, setModalError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setModalError('');
    }
  }, [isOpen]);

  const handleSave = () => {
    if (!newKey.trim()) {
      return;
    }
    if (backendUnavailable) {
      setModalError(integrationT('platformSettings.integrations.mailgun.messages.backendStageUnavailable'));
      return;
    }
    const result = onSave(newKey);
    if (result?.error) {
      setModalError(result.error);
      return;
    }
    setNewKey('');
    setShowKey(false);
    setModalError('');
    onClose();
  };

  const handleOpenChange = (open) => {
    if (!open) {
      setNewKey('');
      setShowKey(false);
      setModalError('');
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Key className="w-5 h-5 text-amber-500" />
            {hasExistingKey ? 'Replace API Key' : 'Add API Key'}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {hasExistingKey 
              ? 'Enter a new Mailgun Private API Key. It will be staged as a pending replacement until you validate domain API access.'
              : 'Enter your Mailgun Private API Key. It will be encrypted and stored securely.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label className="text-zinc-400">Mailgun Private API Key</Label>
            <div className="relative">
              <Input
                type={showKey ? 'text' : 'password'}
                placeholder="key-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white pr-10 font-mono"
                data-testid="new-api-key-input"
              />
              <button
                type="button"
                onClick={() => setShowKey(!showKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
              >
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-zinc-500">
              Find your Private API Key at{' '}
              <a 
                href="https://app.mailgun.com/app/account/security/api_keys" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-amber-400 hover:underline"
              >
                Mailgun API Security <ExternalLink className="w-3 h-3 inline" />
              </a>
            </p>
          </div>

          {hasExistingKey && (
            <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-300">
                The new key will be saved as pending. Use Validate Key Access against your
                Mailgun sending domain, then promote when ready. Your active key stays in use
                until promotion.
              </p>
            </div>
          )}

          <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-red-300">
              <strong>Important:</strong> Use your <u>Private API Key</u> from Mailgun API Security settings.
              <br/>
              Do NOT use: webhook signing key, public verification key, SMTP password, or sending key.
            </div>
          </div>

          {modalError && (
            <div
              className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg"
              data-testid="mailgun-key-modal-error"
            >
              <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-300">{modalError}</p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)} className="border-zinc-600 text-zinc-300">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!newKey.trim()}
            className="bg-amber-500 hover:bg-amber-600 text-black"
            data-testid="save-new-key-btn"
          >
            <Check className="w-4 h-4 mr-2" />
            {hasExistingKey ? 'Stage as Pending' : 'Save Key'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ TEST EMAIL MODAL ============
const TestEmailModal = ({ isOpen, onClose, onSend, defaultEmail, keyType }) => {
  const [email, setEmail] = useState('');
  const [template, setTemplate] = useState('basic');
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // Reset email when modal opens or defaultEmail changes
  useEffect(() => {
    if (isOpen && defaultEmail) {
      setEmail(defaultEmail);
      setLastResult(null);
    }
  }, [isOpen, defaultEmail]);

  const handleSend = async () => {
    if (!email) {
      toast.error('Please enter an email address');
      return;
    }
    
    console.log('[TestEmailModal] Sending test email:', {
      recipient: email,
      template: template,
      keyType: keyType,
      endpoint: '/api/platform/settings/integrations/mailgun/test'
    });
    
    setSending(true);
    setLastResult(null);
    
    try {
      const result = await onSend(email, template, keyType);
      console.log('[TestEmailModal] Response:', result);
      
      setLastResult({
        success: result.success,
        message: result.message,
        error: result.error,
        recipient: email,
        keyTested: result.key_tested || keyType,
        testedAt: result.tested_at,
        mailgunMessageId: result.mailgun_message_id,
        canPromote: result.can_promote_pending
      });
      
      if (result.success) {
        toast.success(`Test email sent to ${email}`);
        if (result.can_promote_pending) {
          toast.success('Pending key has domain access. You can promote it to active.');
        }
        // Don't auto-close - show result first
      } else {
        toast.error(result.error || 'Test email failed');
      }
    } catch (err) {
      console.error('[TestEmailModal] Error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to send test email';
      setLastResult({
        success: false,
        error: errorMessage,
        recipient: email,
        keyTested: keyType
      });
      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const keyLabel = keyType === 'pending' ? 'Pending Replacement Key' : 'Active Key';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Send className="w-5 h-5 text-amber-500" />
            Test {keyLabel}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Send an optional diagnostic test email using the {keyLabel.toLowerCase()}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex items-center gap-2 p-2 bg-zinc-800/50 rounded-lg">
            <Key className={`w-4 h-4 ${keyType === 'pending' ? 'text-amber-400' : 'text-emerald-400'}`} />
            <span className={`text-sm font-medium ${keyType === 'pending' ? 'text-amber-400' : 'text-emerald-400'}`}>
              Testing: {keyLabel}
            </span>
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-400">Recipient Email</Label>
            <Input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-zinc-800 border-zinc-700 text-white"
              data-testid="test-email-recipient"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-400">Email Template</Label>
            <Select value={template} onValueChange={setTemplate}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="basic">Basic Test Email</SelectItem>
                <SelectItem value="onboarding">Agency Onboarding</SelectItem>
                <SelectItem value="password_reset">Password Reset</SelectItem>
                <SelectItem value="billing_alert">Billing Alert</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {keyType === 'pending' && !lastResult && (
            <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-300">
                Test email is optional. Use Validate Key Access on the pending key card to
                confirm Mailgun domain API access before promotion.
              </p>
            </div>
          )}

          {/* Test Result Display */}
          {lastResult && (
            <div className={`p-4 rounded-lg border ${lastResult.success 
              ? 'bg-emerald-500/10 border-emerald-500/30' 
              : 'bg-red-500/10 border-red-500/30'}`}>
              <div className="flex items-center gap-2 mb-3">
                {lastResult.success ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-400" />
                )}
                <span className={`font-semibold ${lastResult.success ? 'text-emerald-400' : 'text-red-400'}`}>
                  {lastResult.success ? 'Test Email Sent Successfully' : 'Test Email Failed'}
                </span>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-500">Recipient:</span>
                  <span className="text-zinc-300 font-mono">{lastResult.recipient}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Key Tested:</span>
                  <span className={`font-medium ${lastResult.keyTested === 'active' ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {lastResult.keyTested === 'active' ? 'Active Key' : 'Pending Key'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Provider:</span>
                  <span className="text-zinc-300">Mailgun (Platform)</span>
                </div>
                {lastResult.testedAt && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Timestamp:</span>
                    <span className="text-zinc-300">{new Date(lastResult.testedAt).toLocaleString()}</span>
                  </div>
                )}
                {lastResult.mailgunMessageId && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Message ID:</span>
                    <span className="text-zinc-400 font-mono text-xs truncate max-w-[200px]" title={lastResult.mailgunMessageId}>
                      {lastResult.mailgunMessageId}
                    </span>
                  </div>
                )}
                {lastResult.error && (
                  <div className="mt-2 p-2 bg-red-500/10 rounded text-xs text-red-300">
                    <strong>Error:</strong> {lastResult.error}
                  </div>
                )}
                {lastResult.success && lastResult.canPromote && (
                  <div className="mt-2 p-2 bg-emerald-500/10 rounded text-xs text-emerald-300">
                    Pending key has domain access. Click Promote to Active when ready.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {lastResult ? (
            <Button onClick={onClose} className="bg-zinc-700 hover:bg-zinc-600 text-white">
              Done
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} className="border-zinc-600 text-zinc-300">
                Cancel
              </Button>
              <Button
                onClick={handleSend}
                disabled={sending || !email}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="send-test-email-btn"
              >
                {sending ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Test
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ MAIN PAGE COMPONENT ============
const PlatformMailgunSettings = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const { authLoading, canFetch, requestConfig } = usePlatformIntegrationAuth();
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [configEndpointUnavailable, setConfigEndpointUnavailable] = useState(false);
  const [retryingConfig, setRetryingConfig] = useState(false);
  const initialFetchDoneRef = useRef(false);
  const fetchInFlightRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testKeyType, setTestKeyType] = useState('active');
  const [newKeyModalOpen, setNewKeyModalOpen] = useState(false);
  const [auditEvents, setAuditEvents] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [validatingAccess, setValidatingAccess] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [showWebhookSigningKey, setShowWebhookSigningKey] = useState(false);
  const [webhookSigningConfiguredLocal, setWebhookSigningConfiguredLocal] = useState(false);
  const [mailgunConfigStatus, setMailgunConfigStatus] = useState(null);

  // Form state — secrets always start blank and are never hydrated from GET responses.
  const [formData, setFormData] = useState(() => createEmptyMailgunFormFields());

  const fetchSettings = useCallback(async ({ isRetry = false } = {}) => {
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    setLoading(!initialLoadComplete);
    if (isRetry) setRetryingConfig(true);
    setConfigEndpointUnavailable(false);
    try {
      const req = requestConfig();
      const settingsRes = await axios.get(`${API}/platform/settings/integrations/mailgun`, req);
      setSettings(settingsRes.data);
      setConfigEndpointUnavailable(false);

      if (settingsRes.data.configured) {
        setFormData(
          createEmptyMailgunFormFields({
            mailgun_domain: settingsRes.data.mailgun_domain || '',
            from_name: settingsRes.data.from_name || '',
            from_email: settingsRes.data.from_email || '',
            reply_to_email: settingsRes.data.reply_to_email || '',
            region: settingsRes.data.region || 'US',
            is_active: settingsRes.data.is_active || false,
            use_as_platform_default: settingsRes.data.use_as_platform_default ?? true,
            use_as_agency_fallback: settingsRes.data.use_as_agency_fallback ?? true,
            track_opens: settingsRes.data.track_opens ?? true,
            track_clicks: settingsRes.data.track_clicks ?? true,
            enable_unsubscribe_tracking: settingsRes.data.enable_unsubscribe_tracking ?? false,
            enable_bounce_logging: settingsRes.data.enable_bounce_logging ?? true,
            environment: settingsRes.data.environment || 'production',
          })
        );
      }

      try {
        const configStatusRes = await axios.get(`${API}/webhooks/mailgun/config-status`, req);
        setMailgunConfigStatus(configStatusRes.data || null);
        if (configStatusRes.data?.signing_key_configured) {
          setWebhookSigningConfiguredLocal(false);
        }
      } catch (_) {
        setMailgunConfigStatus(null);
      }

      try {
        const auditRes = await axios.get(
          `${API}/platform/settings/integrations/mailgun/audit?limit=10`,
          req
        );
        setAuditEvents(auditRes.data.events || []);
      } catch (auditErr) {
        if (auditErr?.response?.status !== 404) {
          console.warn('Mailgun audit load failed:', auditErr);
        }
        setAuditEvents([]);
      }
    } catch (err) {
      if (isIntegrationFetchFailure(err)) {
        setConfigEndpointUnavailable(true);
        setSettings(DEFAULT_UNAVAILABLE_SETTINGS);
        setAuditEvents([]);
      } else {
        setSettings({ configured: false, health_status: 'not_configured' });
        setAuditEvents([]);
      }
    } finally {
      setLoading(false);
      setInitialLoadComplete(true);
      setRetryingConfig(false);
      fetchInFlightRef.current = false;
    }
  }, [requestConfig, initialLoadComplete]);

  useEffect(() => {
    if (authLoading) return;
    if (!canFetch) {
      setLoading(false);
      setInitialLoadComplete(true);
      return;
    }
    if (initialFetchDoneRef.current) return;
    initialFetchDoneRef.current = true;
    fetchSettings();
  }, [authLoading, canFetch, fetchSettings]);

  const handleRetryConfig = () => fetchSettings({ isRetry: true });
  const persistDisabled = configEndpointUnavailable;

  const handleSave = async () => {
    if (persistDisabled) return;

    const hasNewApiKey = Boolean(formData.api_key?.trim());
    const hasNewWebhookSigningKey = Boolean(formData.webhook_signing_key?.trim());
    const hasExistingKey = Boolean(settings?.active_key?.masked);
    const missingFields = [];

    if (!hasExistingKey && !hasNewApiKey) {
      missingFields.push('API Key');
    }

    if (formData.is_active) {
      if (!formData.mailgun_domain?.trim()) {
        missingFields.push('Mailgun Domain');
      }
      if (!formData.from_email?.trim()) {
        missingFields.push('From Email');
      }
    }

    if (formData.from_email?.trim() && !formData.from_name?.trim()) {
      missingFields.push('From Name (required when From Email is set)');
    }

    if (missingFields.length > 0) {
      toast.error(`Please fill in required fields: ${missingFields.join(', ')}`);
      return;
    }

    const savePayload = buildPlatformMailgunSavePayload(formData);

    // Sandbox domain warning
    if (savePayload.mailgun_domain?.includes('sandbox') && savePayload.is_active) {
      toast.warning('Sandbox domains cannot be activated for production. Saving as inactive.');
      savePayload.is_active = false;
    }

    setSaving(true);
    try {
      const res = await axios.post(`${API}/platform/settings/integrations/mailgun`, savePayload, {
        ...requestConfig()
      });
      
      toast.success(res.data.message || 'Mailgun settings saved securely.');

      if (hasNewApiKey && !formData.mailgun_domain?.trim()) {
        toast.info(
          'API key saved. Add a verified sending domain and sender profile before sending live emails.'
        );
      }
      
      if (res.data.domain_warning) {
        toast.warning(res.data.domain_warning);
      }
      
      if (res.data.api_key_action === 'set') {
        toast.success('API key encrypted and stored securely.');
      } else if (res.data.api_key_action === 'replacement_pending') {
        toast.info('New API key saved as pending. Validate domain API access, then promote when ready.');
      }
      
      if (formData.mailgun_domain?.includes('sandbox')) {
        toast.warning('This is a Mailgun sandbox domain. It is for testing only.');
      }

      if (hasNewWebhookSigningKey) {
        setWebhookSigningConfiguredLocal(true);
      }
      
      setHasChanges(false);
      setFormData((prev) => ({
        ...prev,
        api_key: '',
        webhook_signing_key: '',
      }));
      setShowWebhookSigningKey(false);
      fetchSettings();
    } catch (err) {
      // Do not log request bodies or secret fields.
      console.error('[Platform Mailgun] Save failed:', err.response?.status, err.response?.data?.detail || err.message);
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async (email, template, keyType) => {
    if (persistDisabled) {
      throw new Error(integrationT('platformSettings.integrations.mailgun.messages.settingsEndpointUnavailable'));
    }
    const res = await axios.post(
      `${API}/platform/settings/integrations/mailgun/test`,
      {
        recipient_email: email,
        template_type: template,
        key_to_test: keyType,
      },
      requestConfig()
    );

    await fetchSettings();
    return res.data;
  };

  const handleActivate = async (activate) => {
    if (persistDisabled) {
      setFormData((prev) => ({ ...prev, is_active: activate }));
      setHasChanges(true);
      return;
    }
    try {
      await axios.post(`${API}/platform/settings/integrations/mailgun/activate?activate=${activate}`, {}, {
        ...requestConfig()
      });
      toast.success(`Platform Mailgun ${activate ? 'activated' : 'deactivated'}`);
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update activation');
    }
  };

  const handleNewKeySave = (newKey) => {
    if (persistDisabled) {
      return {
        error: integrationT('platformSettings.integrations.mailgun.messages.backendStageUnavailable'),
      };
    }
    setFormData((prev) => ({ ...prev, api_key: newKey }));
    setHasChanges(true);
    toast.info('API key staged. Click "Save Changes" to encrypt and store it.');
    return { success: true };
  };

  const handleValidatePendingKey = async () => {
    if (persistDisabled) return;
    setValidatingAccess(true);
    try {
      const res = await axios.post(
        `${API}/platform/settings/integrations/mailgun/validate-key-access`,
        { key_source: 'pending' },
        requestConfig()
      );
      toast.success(res.data.message || 'Key can access the configured Mailgun domain.');
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to validate key access');
    } finally {
      setValidatingAccess(false);
    }
  };

  const handlePromotePending = async () => {
    if (persistDisabled) return;
    setPromoting(true);
    try {
      const res = await axios.post(`${API}/platform/settings/integrations/mailgun/promote-pending`, {}, {
        ...requestConfig()
      });
      toast.success(res.data.message);
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to promote pending key');
    } finally {
      setPromoting(false);
    }
  };

  const handleCancelPending = async () => {
    if (persistDisabled) return;
    setCancelling(true);
    try {
      const res = await axios.post(`${API}/platform/settings/integrations/mailgun/cancel-pending`, {}, {
        ...requestConfig()
      });
      toast.success(res.data.message);
      setCancelConfirmOpen(false);
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel pending key');
    } finally {
      setCancelling(false);
    }
  };

  const openTestModal = (keyType) => {
    if (persistDisabled) return;
    setTestKeyType(keyType);
    setTestModalOpen(true);
  };

  const updateForm = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setHasChanges(true);
  };

  if (authLoading || (loading && !initialLoadComplete)) {
    return <IntegrationLoadingSpinner />;
  }

  const healthStatus = settings?.health_status || settings?.status || 'not_configured';
  const showSetupNotice =
    !configEndpointUnavailable && !settings?.configured && healthStatus === 'not_configured';
  const pageText = isDark ? 'text-white' : 'text-stone-900';
  const mutedText = isDark ? 'text-zinc-400' : 'text-stone-500';
  const cardClass = isDark ? 'bg-zinc-900/50 border-zinc-800' : 'bg-white border-stone-200';
  const isActive = persistDisabled ? formData.is_active : settings?.is_active;
  const isFailing = healthStatus === 'failing';
  const isAgencyFallback = settings?.use_as_agency_fallback;

  // Extract key info from new API structure
  const activeKey = settings?.active_key || null;
  const pendingKey = settings?.pending_key || null;
  const hasStagedKey = !!formData.api_key;
  const mailgunSaveActive = hasChanges && !persistDisabled && !saving;
  const mailgunSaveLabel = saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'Saved';
  const webhookSigningKeyConfigured = resolveWebhookSigningKeyConfigured({
    settings,
    configStatus: mailgunConfigStatus,
    localConfiguredAfterSave: webhookSigningConfiguredLocal,
  });

  return (
    <div className={`space-y-6 max-w-7xl mx-auto ${pageText}`} data-testid="platform-mailgun-settings">
      {configEndpointUnavailable && (
        <EndpointUnavailableBanner isDark={isDark} onRetry={handleRetryConfig} retrying={retryingConfig} />
      )}
      {showSetupNotice && <IntegrationSetupNotice providerName="Mailgun" />}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-amber-500/20 rounded-xl">
            <Mail className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h1 className={`text-2xl font-bold ${pageText}`}>Mailgun</h1>
            <p className={`text-sm ${mutedText}`}>
              Configure platform-wide email delivery and fallback sender settings
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges || persistDisabled}
            className={platformSaveButtonClassName({
              active: mailgunSaveActive || saving,
              saved: !hasChanges && !persistDisabled && !saving,
              inactive: persistDisabled,
            })}
            data-testid="save-settings-btn"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : hasChanges ? null : (
              <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-400" />
            )}
            {mailgunSaveLabel}
          </Button>
          {persistDisabled && (
            <p className={`text-xs ${isDark ? 'text-amber-400' : 'text-amber-700'}`} data-testid="mailgun-save-disabled-hint">
              {integrationT('platformSettings.integrations.mailgun.messages.backendSaveUnavailable')}
            </p>
          )}
        </div>
      </div>

      {/* Staged Key Banner */}
      {hasStagedKey && (
        <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <Key className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-amber-300">New API Key Staged</p>
            <p className="text-sm text-amber-400/80">
              You have staged a new API key. Click "Save Changes" to encrypt and store it as a pending replacement.
            </p>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setFormData({ ...formData, api_key: '' })}
            className="text-zinc-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Critical Warning Banner */}
      {isFailing && isAgencyFallback && (
        <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
          <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-red-300">Agency Fallback Delivery at Risk</p>
            <p className="text-sm text-red-400/80">
              Platform Mailgun is failing. Platform-to-agency emails and fallback agency emails may not be delivered.
            </p>
          </div>
        </div>
      )}

      {!isActive && settings?.configured && (
        <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-amber-300">Platform Mailgun Inactive</p>
            <p className="text-sm text-amber-400/80">
              System emails may fail unless another platform email provider is configured.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Config */}
        <div className="lg:col-span-2 space-y-6">
          {/* Connection Status Card */}
          <Card className={cardClass}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Server className="w-5 h-5 text-amber-500" />
                  <CardTitle className={pageText}>Connection Status</CardTitle>
                </div>
                <HealthStatusBadge status={healthStatus} />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Provider</p>
                  <p className="text-sm text-white font-medium">Mailgun</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Scope</p>
                  <p className="text-sm text-white font-medium">Platform</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Last Tested</p>
                  <p className="text-sm text-white">
                    {settings?.last_test_at 
                      ? new Date(settings.last_test_at).toLocaleString()
                      : 'Never'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Test Result</p>
                  <p className={`text-sm font-medium ${
                    settings?.last_test_status === 'success' 
                      ? 'text-emerald-400' 
                      : settings?.last_test_status === 'failed'
                        ? 'text-red-400'
                        : 'text-zinc-400'
                  }`}>
                    {settings?.last_test_status || 'N/A'}
                  </p>
                </div>
              </div>
              
              <p className="text-sm text-zinc-400">
                Platform Mailgun is used for system emails, agency admin emails, platform-to-agency communication, 
                and fallback delivery when an agency has not connected its own email provider.
              </p>

              {/* Active Toggle */}
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <div>
                    <Label className="text-white">Active</Label>
                    <p className="text-xs text-zinc-400">Enable Platform Mailgun for email delivery</p>
                  </div>
                  <Switch
                    checked={isActive || false}
                    onCheckedChange={handleActivate}
                    disabled={
                      !persistDisabled
                      && (
                        !settings?.configured
                        || (activeKey?.test_status !== 'passed' && activeKey?.test_status !== 'success')
                      )
                    }
                    data-testid="mailgun-active-toggle"
                  />
                </div>
                {persistDisabled && (
                  <p className="text-xs text-amber-400 px-1" data-testid="mailgun-active-toggle-hint">
                    {integrationT('platformSettings.integrations.mailgun.messages.activeToggleUnavailable')}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* API Keys Section - New Separated Design */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-500" />
              <h2 className="text-lg font-semibold text-white">API Key Management</h2>
            </div>
            <p className="text-sm text-zinc-400">
              API keys are encrypted at rest and never displayed in full. Use fingerprints to verify key identity.
            </p>

            {/* Active Key Card */}
            <ActiveKeyCard
              keyInfo={activeKey}
              onTest={() => openTestModal('active')}
              onReplace={() => setNewKeyModalOpen(true)}
              testing={false}
              persistDisabled={persistDisabled}
            />

            {/* Pending Key Card */}
            <PendingKeyCard
              keyInfo={pendingKey}
              mailgunDomain={settings?.mailgun_domain || formData.mailgun_domain}
              onValidateAccess={handleValidatePendingKey}
              onTest={() => openTestModal('pending')}
              onPromote={handlePromotePending}
              onCancel={() => setCancelConfirmOpen(true)}
              validating={validatingAccess}
              testing={false}
              promoting={promoting}
              cancelling={cancelling}
              persistDisabled={persistDisabled}
            />
          </div>

          {/* Domain Settings Card */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-amber-500" />
                  <div>
                    <CardTitle className="text-white">Domain Settings</CardTitle>
                    <CardDescription className="text-zinc-400">
                      Sending domain and region. Full DNS verification is on the Domains page.
                    </CardDescription>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-zinc-700 text-zinc-300 shrink-0"
                  onClick={() => navigate('/platform/settings/communication/domains')}
                  data-testid="mailgun-manage-domains-link"
                >
                  Manage Domains
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Mailgun Sending Domain</Label>
                  <Input
                    placeholder="mg.fundelite.com"
                    value={formData.mailgun_domain}
                    onChange={(e) => updateForm('mailgun_domain', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="mailgun-domain-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Region</Label>
                  <Select
                    value={formData.region}
                    onValueChange={(v) => updateForm('region', v)}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="mailgun-region-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="US">United States</SelectItem>
                      <SelectItem value="EU">European Union</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Default Sender Profile Card */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-amber-500" />
                <div>
                  <CardTitle className="text-white">Default Sender Profile</CardTitle>
                  <CardDescription className="text-zinc-400">
                    Optional platform defaults when automations or templates do not specify a sender.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Default From Name</Label>
                  <Input
                    placeholder="FundElite"
                    value={formData.from_name}
                    onChange={(e) => updateForm('from_name', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="from-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Default From Email</Label>
                  <Input
                    type="email"
                    placeholder="no-reply@fundelite.com"
                    value={formData.from_email}
                    onChange={(e) => updateForm('from_email', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="from-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Default Reply-To Email</Label>
                <Input
                  type="email"
                  placeholder="support@fundelite.com"
                  value={formData.reply_to_email}
                  onChange={(e) => updateForm('reply_to_email', e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="reply-to-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Delivery Settings Card */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <Settings className="w-5 h-5 text-amber-500" />
                <CardTitle className="text-white">Delivery Settings</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <div>
                    <Label className="text-white">Platform Default Sender</Label>
                    <p className="text-xs text-zinc-400">Use for all platform system emails</p>
                  </div>
                  <Switch
                    checked={formData.use_as_platform_default}
                    onCheckedChange={(v) => updateForm('use_as_platform_default', v)}
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <div>
                    <Label className="text-white">Agency Fallback Sender</Label>
                    <p className="text-xs text-zinc-400">Fallback when agency Mailgun unavailable</p>
                  </div>
                  <Switch
                    checked={formData.use_as_agency_fallback}
                    onCheckedChange={(v) => updateForm('use_as_agency_fallback', v)}
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <div>
                    <Label className="text-white">Track Opens</Label>
                    <p className="text-xs text-zinc-400">Track email open events</p>
                  </div>
                  <Switch
                    checked={formData.track_opens}
                    onCheckedChange={(v) => updateForm('track_opens', v)}
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <div>
                    <Label className="text-white">Track Clicks</Label>
                    <p className="text-xs text-zinc-400">Track link click events</p>
                  </div>
                  <Switch
                    checked={formData.track_clicks}
                    onCheckedChange={(v) => updateForm('track_clicks', v)}
                  />
                </div>
                <div
                  className="md:col-span-2 space-y-2 p-3 bg-zinc-800/50 rounded-lg"
                  data-testid="mailgun-webhook-signing-key-section"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Label htmlFor="mailgun-webhook-signing-key" className="text-white">
                        HTTP Webhook Signing Key
                      </Label>
                      <p className="text-xs text-zinc-400">
                        Used to verify Mailgun open/click webhook signatures. Leave blank to keep the
                        existing key.
                      </p>
                    </div>
                    {webhookSigningKeyConfigured && (
                      <Badge
                        className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 shrink-0"
                        data-testid="mailgun-webhook-signing-key-configured"
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Configured
                      </Badge>
                    )}
                  </div>
                  <div className="relative">
                    <Input
                      id="mailgun-webhook-signing-key"
                      type={showWebhookSigningKey ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder={
                        webhookSigningKeyConfigured
                          ? 'Enter a new key to replace the existing one'
                          : 'Paste Mailgun HTTP webhook signing key'
                      }
                      value={formData.webhook_signing_key || ''}
                      onChange={(e) => updateForm('webhook_signing_key', e.target.value)}
                      className="bg-zinc-800 border-zinc-700 text-white pr-10 font-mono"
                      data-testid="mailgun-webhook-signing-key-input"
                      disabled={persistDisabled}
                    />
                    <button
                      type="button"
                      onClick={() => setShowWebhookSigningKey((prev) => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                      aria-label={
                        showWebhookSigningKey
                          ? 'Hide webhook signing key'
                          : 'Show webhook signing key'
                      }
                      data-testid="mailgun-webhook-signing-key-toggle"
                    >
                      {showWebhookSigningKey ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-zinc-500">
                    Find this in Mailgun → Sending → Webhooks → HTTP webhook signing key. The stored
                    key is never shown here.
                  </p>
                </div>
                <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <div>
                    <Label className="text-white">Bounce Logging</Label>
                    <p className="text-xs text-zinc-400">Log bounce events for analysis</p>
                  </div>
                  <Switch
                    checked={formData.enable_bounce_logging}
                    onCheckedChange={(v) => updateForm('enable_bounce_logging', v)}
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <div>
                    <Label className="text-white">Environment</Label>
                    <p className="text-xs text-zinc-400">Deployment environment</p>
                  </div>
                  <Select
                    value={formData.environment}
                    onValueChange={(v) => updateForm('environment', v)}
                  >
                    <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="development">Development</SelectItem>
                      <SelectItem value="staging">Staging</SelectItem>
                      <SelectItem value="production">Production</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Status & Info */}
        <div className="space-y-6">
          {/* Domain Verification Card */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-amber-500" />
                  <CardTitle className="text-white text-base">Domain Verification</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <DomainVerificationPanel
                domainStatus={settings?.domain_status}
                onManageDomains={() => navigate('/platform/settings/communication/domains')}
                manageDomainsPath="/platform/settings/communication/domains"
                showMailgunLink
              />
            </CardContent>
          </Card>

          {/* Email Routing Usage Card */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <ArrowRight className="w-5 h-5 text-amber-500" />
                <CardTitle className="text-white text-base">Email Routing</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs text-emerald-400 uppercase mb-2 font-semibold">Platform Mailgun Sends:</p>
                <ul className="space-y-1">
                  {[
                    'Platform system emails',
                    'Platform-to-agency emails',
                    'Agency owner onboarding',
                    'Agency admin password resets',
                    'Billing/system alerts',
                    'Fallback delivery for agencies'
                  ].map((item, i) => (
                    <li key={i} className="text-xs text-zinc-400 flex items-center gap-2">
                      <Check className="w-3 h-3 text-emerald-400" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <p className="text-xs text-red-400 uppercase mb-2 font-semibold">Does NOT Send (when agency Mailgun active):</p>
                <ul className="space-y-1">
                  {[
                    'Agency-branded client emails',
                    'Self-repair signup emails',
                    'Client portal notifications'
                  ].map((item, i) => (
                    <li key={i} className="text-xs text-zinc-500 flex items-center gap-2">
                      <X className="w-3 h-3 text-zinc-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity Card */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <Activity className="w-5 h-5 text-amber-500" />
                <CardTitle className="text-white text-base">Recent Activity</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {auditEvents.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-4">No recent activity</p>
              ) : (
                <div className="space-y-3">
                  {auditEvents.slice(0, 5).map((event, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm">
                      <div className={`p-1 rounded ${
                        event.action?.includes('failed') ? 'bg-red-500/20' :
                        event.action?.includes('activated') ? 'bg-emerald-500/20' :
                        'bg-zinc-700'
                      }`}>
                        {event.action?.includes('failed') ? (
                          <XCircle className="w-3 h-3 text-red-400" />
                        ) : event.action?.includes('test') ? (
                          <Send className="w-3 h-3 text-blue-400" />
                        ) : (
                          <Settings className="w-3 h-3 text-zinc-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-zinc-300 truncate">
                          {event.action?.replace(/_/g, ' ')}
                        </p>
                        <p className="text-xs text-zinc-500">
                          {event.timestamp && new Date(event.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Test Email Modal */}
      <TestEmailModal
        isOpen={testModalOpen}
        onClose={() => setTestModalOpen(false)}
        onSend={handleSendTest}
        defaultEmail={user?.email}
        keyType={testKeyType}
      />

      {/* New Key Modal */}
      <NewKeyModal
        isOpen={newKeyModalOpen}
        onClose={() => setNewKeyModalOpen(false)}
        onSave={handleNewKeySave}
        hasExistingKey={!!activeKey?.masked}
        backendUnavailable={persistDisabled}
      />

      {/* Cancel Pending Key Confirmation */}
      <AlertDialog open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Cancel Pending Replacement?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will permanently remove the pending replacement key. Your active key will remain unchanged and continue to be used for email delivery.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-600 text-zinc-300 hover:bg-zinc-800">
              Keep Pending Key
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelPending}
              disabled={cancelling}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-cancel-pending-btn"
            >
              {cancelling ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Cancel Pending Key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PlatformMailgunSettings;
